export type CameraState =
  | "idle"
  | "starting"
  | "running"
  | "stopping"
  | "error";

export interface CameraManagerConfig {
  videoElement: HTMLVideoElement;
  canvasElement: HTMLCanvasElement;
  canvasCtx: CanvasRenderingContext2D;
  onFrame: () => Promise<void>;
  onResize: (trigger: string) => void;
  getDiagTime: () => string;
}

// Detect if running on iOS Safari
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// Detect if running on actual iPhone (not iPad or Mac with external webcam)
const isIPhone = /iPhone/.test(navigator.userAgent) &&
  !/iPad/.test(navigator.userAgent);

type NativeCameraHandle = { start(): void; stop(): void };

declare global {
  // Survives HMR so the new bundle can stop the previous native Camera wrapper.
  var __cameraInstance: NativeCameraHandle | null | undefined;
}

export class CameraManager {
  private _state: CameraState = "idle";
  private currentMediaStream: MediaStream | null = null;
  private currentDeviceId: string | null = null;
  private cameraPermissionRequested = false;
  private videoMetadataListenerAdded = false;
  private enableCamera = true;
  private nativeCamera: NativeCameraHandle | null = null;

  private cfg: CameraManagerConfig;

  constructor(config: CameraManagerConfig) {
    this.cfg = config;
    this.setupVideoMetadataListener();
  }

  /** Instance field plus `globalThis.__cameraInstance` (latter survives HMR). */
  private getNativeCameraHandle(): NativeCameraHandle | null {
    return this.nativeCamera ?? globalThis.__cameraInstance ?? null;
  }

  private setNativeCameraHandle(cam: NativeCameraHandle | null): void {
    this.nativeCamera = cam;
    globalThis.__cameraInstance = cam;
  }

  get state(): CameraState {
    return this._state;
  }

  get isRunning(): boolean {
    return this._state === "running";
  }

  applyOptions(opts: { enableCamera: boolean }): void {
    this.enableCamera = opts.enableCamera;
    if (opts.enableCamera && !this.isRunning && this.state !== "starting") {
      console.log(
        `📸 camera applyOptions: enableCamera=${opts.enableCamera} state=${this._state} → calling start()`,
        new Error("camera start() call stack"),
      );
      this.start().catch((err) =>
        console.error("📸 camera start() failed:", err)
      );
    } else if (!opts.enableCamera && this.isRunning) {
      this.stop();
    }
  }

  async start(): Promise<void> {
    console.log(`📷 CameraManager.start() state=${this._state}`);
    if (this._state === "running" || this._state === "starting") {
      console.log(
        `📷 CameraManager.start() no-op, already ${this._state}`,
      );
      return;
    }
    // Set "starting" synchronously before any await so concurrent start()
    // calls from rapid options-changed events are blocked by the guard above.
    this._state = "starting";
    console.log(`📷 CameraManager.start() → starting`);
    await this.startCamera();
  }

  stop(): void {
    console.log(`📸 CameraManager.stop()`);
    if (this._state === "idle" || this._state === "stopping") {
      console.log(
        `[CameraManager.stop] no-op, already ${this._state}`,
      );
      return;
    }
    this._state = "stopping";
    this.cleanupCamera();
    this.cfg.canvasCtx.clearRect(
      0,
      0,
      this.cfg.canvasElement.width,
      this.cfg.canvasElement.height,
    );
    // Allow re-enabling
    this.cameraPermissionRequested = false;
    this._state = "idle";
  }

  cleanup(keepStream = false): void {
    console.log(`📸 CameraManager.cleanup()`);
    this.cleanupCamera(keepStream);
  }

  handleHmrRestore(): void {
    console.log(
      `📸 CameraManager.handleHmrRestore() enableCamera=${this.enableCamera}`,
    );
    if (globalThis.__cameraInstance) {
      if (!this.cfg.videoElement.srcObject) {
        this.cleanupCamera();
      } else {
        const stream = this.cfg.videoElement.srcObject as MediaStream;
        if (!stream.active || stream.getVideoTracks().length === 0) {
          this.cleanupCamera();
        } else {
          // Stream is still active, preserve it and just clean up the camera instance
          const cameraInstance = globalThis.__cameraInstance;
          if (cameraInstance && typeof cameraInstance.stop === "function") {
            try {
              cameraInstance.stop();
            } catch (err) {
              console.warn("Error stopping camera instance:", err);
            }
          }
          this.setNativeCameraHandle(null);
          if (!this.currentDeviceId) {
            const videoTrack = stream.getVideoTracks()[0];
            if (videoTrack && videoTrack.getSettings().deviceId) {
              this.currentDeviceId = videoTrack.getSettings().deviceId!;
            }
          }
          this.currentMediaStream = stream;
        }
      }
    }
  }

  private setupVideoMetadataListener(): void {
    console.log(`📸 CameraManager.setupVideoMetadataListener()`);
    if (this.videoMetadataListenerAdded) return;
    this.videoMetadataListenerAdded = true;

    this.cfg.videoElement.addEventListener("loadedmetadata", () => {
      const newWidth = this.cfg.videoElement.videoWidth;
      const newHeight = this.cfg.videoElement.videoHeight;
      console.log(
        `📸 Video metadata loaded @${this.cfg.getDiagTime()}s: ${newWidth}x${newHeight}`,
      );
      if (
        newWidth > 0 &&
        newHeight > 0 &&
        (this.cfg.canvasElement.width !== newWidth ||
          this.cfg.canvasElement.height !== newHeight)
      ) {
        this.cfg.canvasElement.width = newWidth;
        this.cfg.canvasElement.height = newHeight;
        this.cfg.onResize("video-metadata");
      }
    });
  }

  private cleanupCamera(keepStream = false): void {
    console.log(
      `📸 CameraManager.cleanupCamera() enableCamera=${this.enableCamera}`,
    );
    const nativeCam = this.getNativeCameraHandle();
    if (nativeCam && typeof nativeCam.stop === "function") {
      try {
        nativeCam.stop();
      } catch (err) {
        console.warn("Error stopping camera:", err);
      }
    }

    if (!keepStream) {
      if (this.cfg.videoElement.srcObject) {
        const stream = this.cfg.videoElement.srcObject as MediaStream;
        if (stream && stream.getTracks) {
          stream.getTracks().forEach((track) => {
            track.stop();
          });
        }
        this.cfg.videoElement.srcObject = null;
      }

      if (this.currentMediaStream) {
        this.currentMediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.currentMediaStream = null;
      }
      this.currentDeviceId = null;
    }

    this.setNativeCameraHandle(null);
    this._state = "idle";
  }

  private async checkCameraPermission(): Promise<boolean> {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        if (result.state === "denied") {
          console.warn("Camera permission denied by user");
          return false;
        }
      }
      return true;
    } catch (_err) {
      console.debug(
        "Permissions API not available, proceeding with camera access",
      );
      return true;
    }
  }

  private async startCamera(): Promise<void> {
    const existingStream = (this.cfg.videoElement.srcObject ||
      this.currentMediaStream) as MediaStream | null;
    if (
      existingStream &&
      existingStream.active &&
      existingStream.getVideoTracks().length > 0
    ) {
      const videoTrack = existingStream.getVideoTracks()[0];
      if (videoTrack.readyState === "live") {
        if (!this.cfg.videoElement.srcObject) {
          this.cfg.videoElement.srcObject = existingStream;
        }

        const nativeCam = this.getNativeCameraHandle();
        if (nativeCam && typeof nativeCam.start === "function") {
          this.setNativeCameraHandle(nativeCam);
          try {
            nativeCam.start();
            this._state = "running";
            return;
          } catch (err) {
            console.warn("Error starting camera instance:", err);
          }
        } else {
          await this.createAndStartCamera();
          return;
        }
      }
    }

    await this.createAndStartCamera();
  }

  private async createAndStartCamera(): Promise<void> {
    console.log(
      `📸 createAndStartCamera called (state=${this._state})`,
    );

    const existingStream = (this.cfg.videoElement.srcObject ||
      this.currentMediaStream) as MediaStream | null;
    if (
      existingStream &&
      existingStream.active &&
      existingStream.getVideoTracks().length > 0
    ) {
      const videoTrack = existingStream.getVideoTracks()[0];
      if (videoTrack.readyState === "live") {
        const trackSettings = videoTrack.getSettings();
        console.log(
          `📸 Reusing existing camera stream: ${trackSettings.width}x${trackSettings.height} (deviceId: ${
            trackSettings.deviceId || "unknown"
          })`,
        );
        if (!this.cfg.videoElement.srcObject) {
          this.cfg.videoElement.srcObject = existingStream;
        }

        await new Promise<void>((resolve) => {
          if (this.cfg.videoElement.readyState >= 2) {
            resolve();
          } else {
            this.cfg.videoElement.addEventListener(
              "loadedmetadata",
              () => resolve(),
              { once: true },
            );
          }
        });

        const actualWidth = this.cfg.videoElement.videoWidth ||
          videoTrack.getSettings().width || 1280;
        const actualHeight = this.cfg.videoElement.videoHeight ||
          videoTrack.getSettings().height || 720;

        if (
          this.cfg.canvasElement.width !== actualWidth ||
          this.cfg.canvasElement.height !== actualHeight
        ) {
          this.cfg.canvasElement.width = actualWidth;
          this.cfg.canvasElement.height = actualHeight;
          this.cfg.onResize("camera-init-reuse");
        }

        const camera = new (globalThis as any).Camera(this.cfg.videoElement, {
          onFrame: async () => {
            if (!this.isRunning) return;
            if (!this.enableCamera) return;
            await this.cfg.onFrame();
          },
          width: actualWidth,
          height: actualHeight,
        });

        this.setNativeCameraHandle(camera);

        try {
          camera.start();
          this._state = "running";
          return;
        } catch (err) {
          console.warn("Error starting camera with existing stream:", err);
        }
      }
    }

    if (this.getNativeCameraHandle()) {
      this.cleanupCamera(true);
      this._state = "starting"; // cleanupCamera resets to idle
    }

    const hasPermission = await this.checkCameraPermission();
    if (!hasPermission) {
      console.warn(
        "Camera permission denied. Please grant camera access to use this feature.",
      );
      this._state = "error";
      return;
    }

    let stream: MediaStream | null = null;

    let constraintMode: "portrait" | "landscape" | "default";
    let isPortrait: boolean | undefined = undefined;
    let deviceOrientation: number | undefined = undefined;

    const baseVideoConstraints: MediaTrackConstraints = isIPhone
      ? (() => {
        deviceOrientation = window.screen?.orientation?.angle ??
          (window as any).orientation ?? 0;
        isPortrait = deviceOrientation === 0 || deviceOrientation === 180;

        if (isPortrait) {
          constraintMode = "portrait";
          return {
            facingMode: "user",
            width: { ideal: 1080 },
            height: { ideal: 1920 },
          };
        } else {
          constraintMode = "landscape";
          return {
            facingMode: "user",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          };
        }
      })()
      : (() => {
        constraintMode = "landscape";
        isPortrait = false;
        return {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        };
      })();

    const orientationInfo = isIPhone
      ? `orientation=${deviceOrientation}, isPortrait=${isPortrait}`
      : `viewport=${window.innerWidth}x${window.innerHeight}, always landscape for full horizontal width`;
    console.debug(
      `📸 Camera constraints mode: ${constraintMode} (isIPhone=${isIPhone}, ${orientationInfo})`,
      baseVideoConstraints,
    );

    if (this.cameraPermissionRequested) {
      console.debug(
        `📸 Camera permissions already requested, skipping`,
      );
      return;
    }
    this.cameraPermissionRequested = true;
    console.log(
      `📸 Requesting camera permissions @${this.cfg.getDiagTime()}s`,
    );

    if (this.currentDeviceId) {
      const preferredConstraints: MediaStreamConstraints = {
        video: {
          ...baseVideoConstraints,
          deviceId: { ideal: this.currentDeviceId },
        },
      };

      try {
        stream = await navigator.mediaDevices.getUserMedia(
          preferredConstraints,
        );
        const videoTrack = stream.getVideoTracks()[0];
        if (
          videoTrack &&
          videoTrack.getSettings().deviceId !== this.currentDeviceId
        ) {
          if (videoTrack.getSettings().deviceId) {
            this.currentDeviceId = videoTrack.getSettings().deviceId!;
          }
        }
      } catch (_err: any) {
        console.warn(
          "Could not use stored camera device, requesting new selection",
        );
        this.currentDeviceId = null;
        const fallbackConstraints: MediaStreamConstraints = {
          video: baseVideoConstraints,
        };
        stream = await navigator.mediaDevices.getUserMedia(
          fallbackConstraints,
        );
      }
    } else {
      const constraints: MediaStreamConstraints = {
        video: baseVideoConstraints,
      };
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    }

    this.currentMediaStream = stream;
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack && videoTrack.getSettings().deviceId) {
      this.currentDeviceId = videoTrack.getSettings().deviceId!;
    }

    this.cfg.videoElement.srcObject = stream;

    await new Promise<void>((resolve) => {
      if (
        this.cfg.videoElement.readyState >= 2 &&
        this.cfg.videoElement.videoWidth > 0
      ) {
        resolve();
      } else {
        const onMetadataLoaded = () => {
          if (isIOS) {
            requestAnimationFrame(() => {
              resolve();
            });
          } else {
            resolve();
          }
        };
        this.cfg.videoElement.addEventListener(
          "loadedmetadata",
          onMetadataLoaded,
          { once: true },
        );
      }
    });

    if (isIOS) {
      try {
        await this.cfg.videoElement.play();
      } catch (err) {
        console.debug("Auto-play attempt:", err);
      }
    }

    let actualWidth = this.cfg.videoElement.videoWidth;
    let actualHeight = this.cfg.videoElement.videoHeight;

    if (!actualWidth || !actualHeight) {
      const settings = videoTrack.getSettings();
      actualWidth = settings.width || 1280;
      actualHeight = settings.height || 720;
    }

    // Get camera capabilities for logging
    if (typeof videoTrack.getCapabilities === "function") {
      try {
        videoTrack.getCapabilities();
      } catch (e) {
        console.debug("Could not get camera capabilities:", e);
      }
    }

    console.log(
      `📸 Camera stream established @${this.cfg.getDiagTime()}s: ${actualWidth}x${actualHeight}`,
    );

    if (
      this.cfg.canvasElement.width !== actualWidth ||
      this.cfg.canvasElement.height !== actualHeight
    ) {
      this.cfg.canvasElement.width = actualWidth;
      this.cfg.canvasElement.height = actualHeight;
      this.cfg.onResize("camera-init-new");
    }

    const camera = new (globalThis as any).Camera(this.cfg.videoElement, {
      onFrame: async () => {
        if (!this.isRunning) return;
        const enableCamera = globalThis.options?.enableCamera !== false;
        if (!enableCamera) return;
        await this.cfg.onFrame();
      },
      width: actualWidth,
      height: actualHeight,
    });

    this.setNativeCameraHandle(camera);

    let cameraStarted = false;
    let cameraError: any = null;

    const errorHandler = (event: Event | Error) => {
      const error = event instanceof Error ? event : (event as any).error;
      cameraError = error;
      cameraStarted = false;
      this._state = "error";

      if (
        error?.name === "NotAllowedError" ||
        error?.message?.includes("Permission denied") ||
        error?.message?.includes("NotAllowedError")
      ) {
        console.warn(
          "Camera access denied. Please grant camera permissions in your browser settings.",
        );
      } else if (
        error?.name === "NotFoundError" ||
        error?.message?.includes("NotFoundError")
      ) {
        console.warn("No camera found. Please connect a camera device.");
      } else if (
        error?.name === "NotReadableError" ||
        error?.message?.includes("NotReadableError")
      ) {
        console.warn("Camera is already in use by another application.");
      } else {
        console.warn("Error starting camera:", error || event);
      }
    };

    this.cfg.videoElement.addEventListener("error", errorHandler, {
      once: true,
    });

    try {
      camera.start();

      await new Promise<void>((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (this.cfg.videoElement.srcObject) {
            const stream = this.cfg.videoElement.srcObject as MediaStream;
            if (stream.active && stream.getVideoTracks().length > 0) {
              if (
                this.cfg.videoElement.readyState >= 2 &&
                this.cfg.videoElement.videoWidth > 0
              ) {
                clearInterval(checkInterval);
                this.cfg.videoElement.removeEventListener(
                  "error",
                  errorHandler,
                );
                cameraStarted = true;

                const actualWidth = this.cfg.videoElement.videoWidth;
                const actualHeight = this.cfg.videoElement.videoHeight;
                if (
                  this.cfg.canvasElement.width !== actualWidth ||
                  this.cfg.canvasElement.height !== actualHeight
                ) {
                  this.cfg.canvasElement.width = actualWidth;
                  this.cfg.canvasElement.height = actualHeight;
                  this.cfg.onResize("ios-video-ready");
                }

                resolve();
              }
            }
          }
        }, 50);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!cameraStarted && !cameraError) {
            this.cfg.videoElement.removeEventListener("error", errorHandler);
            resolve();
          } else if (cameraError) {
            reject(cameraError);
          } else {
            resolve();
          }
        }, 2000);
      });

      this._state = cameraStarted ? "running" : "idle";
    } catch (err: any) {
      errorHandler(err);
    }
  }
}
